import type { IsoDate } from "../dates";
import {
  deriveId,
  type AccountId,
  type CategoryId,
  type ImportBatchId,
  type PayeeId,
  type ScheduledId,
  type TransactionId,
} from "../ids";
import type {
  Account,
  Cleared,
  Flag,
  GeoLocation,
  ImportNote,
  Income,
  SplitLine,
  Transaction,
} from "../schemas";
import { CommandError, type DocWriter } from "../doc";

export interface SplitInput {
  amount: number;
  categoryId?: CategoryId;
  income?: Income;
  transferAccountId?: AccountId;
  payeeId?: PayeeId;
  memo?: string;
}

export interface TransactionInput {
  id: TransactionId;
  accountId: AccountId;
  date: IsoDate;
  amount: number;
  splits?: SplitInput[];
  payeeId?: PayeeId;
  categoryId?: CategoryId;
  income?: Income;
  /** Makes this a transfer to/from that account; the counterpart is managed automatically. */
  transferAccountId?: AccountId;
  memo?: string;
  cleared?: Cleared;
  flag?: Flag;
  approved?: boolean;
  location?: GeoLocation;
  importNote?: ImportNote;
  importKey?: string;
  importBatchId?: ImportBatchId;
  scheduleId?: ScheduledId;
  occurrence?: IsoDate;
  createdBy?: string;
}

export interface SaveOptions {
  /** Required to change the amount, date or account of a reconciled transaction. */
  confirmReconciled?: boolean;
}

export class ReconciledLockedError extends CommandError {
  constructor() {
    super("This transaction is reconciled. Confirm to change its amount, date or account.");
    this.name = "ReconciledLockedError";
  }
}

/**
 * SPEC §4: a transfer between an on-budget and a tracking account carries a category (or, for
 * money coming in, income) on the on-budget side. Only enforced when the transfer is created,
 * so ones saved by older clients without a category can still be edited.
 */
export class TrackingTransferCategoryError extends CommandError {
  constructor() {
    super("A transfer to a tracking account needs a category");
    this.name = "TrackingTransferCategory";
  }
}

function account(w: DocWriter, id: AccountId): Account {
  const a = w.get("accounts", id);
  if (!a || a.deleted) {
    throw new CommandError("Account not found");
  }
  return a;
}

/** The parent of a split-line transfer counterpart, or null. */
export function splitParentOf(w: Pick<DocWriter, "get">, t: Transaction): Transaction | null {
  if (!t.transfer) {
    return null;
  }
  const parent = w.get("transactions", t.transfer.txnId);
  if (parent && parent.amountAndSplits.splits.some((s) => s.transferTxnId === t.id)) {
    return parent;
  }
  return null;
}

/**
 * Creates or updates a transaction, keeping transfer counterparts (including split-line
 * transfers) in step. SPEC §4 Transfers: the category sits on the on-budget side, and a
 * transfer between two on-budget accounts has none.
 */
export function saveTransaction(
  w: DocWriter,
  input: TransactionInput,
  options: SaveOptions = {},
): Transaction {
  const existing = w.get("transactions", input.id);
  const live = existing && !existing.deleted ? existing : null;
  const own = account(w, input.accountId);
  const splitsIn = input.splits ?? [];

  if (live) {
    const parent = splitParentOf(w, live);
    if (parent) {
      return saveSplitCounterpart(w, live, input);
    }
    const moved =
      live.amountAndSplits.amount !== input.amount ||
      live.date !== input.date ||
      live.accountId !== input.accountId;
    if (live.cleared === "reconciled" && moved && !options.confirmReconciled) {
      throw new ReconciledLockedError();
    }
  }
  if (input.income && input.categoryId) {
    throw new CommandError("A transaction is either income or has a category, not both");
  }
  if (input.transferAccountId === input.accountId) {
    throw new CommandError("A transfer needs a different account");
  }
  if (splitsIn.length > 0) {
    if (input.transferAccountId || input.categoryId || input.income) {
      throw new CommandError("A split transaction has its categories on the split lines");
    }
    const total = splitsIn.reduce((s, l) => s + l.amount, 0);
    if (total !== input.amount) {
      throw new CommandError("The split lines must add up to the transaction amount");
    }
    for (const s of splitsIn) {
      if (s.income && s.categoryId) {
        throw new CommandError("A split line is either income or has a category, not both");
      }
      if (s.transferAccountId === input.accountId) {
        throw new CommandError("A split transfer needs a different account");
      }
    }
  }

  // Thrown after the reconciled-lock check, which takes precedence.
  let categoryMissing = false;
  // Top-level transfer.
  const oldCounterpartId =
    live?.transfer && live.amountAndSplits.splits.length === 0 ? live.transfer.txnId : undefined;
  let transfer: Transaction["transfer"];
  let categoryId = input.categoryId;
  let income = input.income;
  let counterpartCategory: CategoryId | undefined;
  let counterpartIncome: Income | undefined;
  if (input.transferAccountId) {
    const other = account(w, input.transferAccountId);
    const keep =
      oldCounterpartId && w.get("transactions", oldCounterpartId)?.accountId === other.id;
    // Derived, so two devices creating the same transfer (a scheduled occurrence) merge.
    const counterpartId = keep
      ? (oldCounterpartId as TransactionId)
      : deriveId("transaction", input.id, "transfer", other.id);
    transfer = { accountId: other.id, txnId: counterpartId };
    if (own.onBudget && other.onBudget) {
      categoryId = undefined;
      income = undefined;
    } else if (!own.onBudget && other.onBudget) {
      const kept = keep && !categoryId && !income ? w.get("transactions", counterpartId) : null;
      counterpartCategory = categoryId ?? kept?.categoryId;
      counterpartIncome = income ?? (counterpartCategory ? undefined : kept?.income);
      categoryId = undefined;
      income = undefined;
    } else if (!own.onBudget && !other.onBudget) {
      categoryId = undefined;
      income = undefined;
    }
    const isNewTransfer = live?.transfer?.accountId !== other.id || !keep;
    const onBudgetSideEmpty = own.onBudget
      ? !categoryId && !income
      : !counterpartCategory && !counterpartIncome;
    if (own.onBudget !== other.onBudget && onBudgetSideEmpty && isNewTransfer) {
      categoryMissing = true;
    }
  }

  // Split-line transfers.
  const oldSplitCounterparts = new Map<string, SplitLine>();
  for (const s of live?.amountAndSplits.splits ?? []) {
    if (s.transferTxnId) {
      oldSplitCounterparts.set(s.transferTxnId, s);
    }
  }
  const usedOld = new Set<string>();
  const linesPerAccount = new Map<string, number>();
  const splits: SplitLine[] = splitsIn.map((s) => {
    const line: SplitLine = { amount: s.amount, memo: s.memo ?? "" };
    if (s.payeeId) {
      line.payeeId = s.payeeId;
    }
    if (s.transferAccountId) {
      const other = account(w, s.transferAccountId);
      line.transferAccountId = other.id;
      const reuse = [...oldSplitCounterparts.entries()].find(
        ([id, old]) => !usedOld.has(id) && old.transferAccountId === other.id,
      );
      if (!reuse && own.onBudget !== other.onBudget && !s.categoryId) {
        categoryMissing = true;
      }
      const index = linesPerAccount.get(other.id) ?? 0;
      linesPerAccount.set(other.id, index + 1);
      line.transferTxnId =
        (reuse?.[0] as TransactionId | undefined) ??
        deriveId("transaction", input.id, "split", other.id, String(index));
      usedOld.add(line.transferTxnId);
      if (s.categoryId && own.onBudget !== other.onBudget) {
        line.categoryId = s.categoryId;
      }
    } else if (s.income) {
      line.income = s.income;
    } else if (s.categoryId) {
      line.categoryId = s.categoryId;
    }
    return line;
  });

  if (!options.confirmReconciled) {
    const next = new Map<string, CounterpartPlacement>();
    if (transfer && splits.length === 0) {
      next.set(transfer.txnId, { accountId: transfer.accountId, amount: -input.amount });
    }
    for (const s of splits) {
      if (s.transferAccountId && s.transferTxnId) {
        next.set(s.transferTxnId, { accountId: s.transferAccountId, amount: -s.amount });
      }
    }
    const old = [...(oldCounterpartId ? [oldCounterpartId] : []), ...oldSplitCounterparts.keys()];
    for (const id of old) {
      guardReconciledCounterpart(w, id, next.get(id) ?? null, input.date);
    }
  }
  if (categoryMissing) {
    throw new TrackingTransferCategoryError();
  }

  const txn = w.put("transactions", input.id, {
    accountId: input.accountId,
    date: input.date,
    amountAndSplits: { amount: input.amount, splits },
    payeeId: input.payeeId,
    categoryId: splits.length > 0 ? undefined : categoryId,
    income: splits.length > 0 ? undefined : income,
    transfer: splits.length > 0 ? undefined : transfer,
    memo: input.memo ?? live?.memo ?? "",
    cleared: input.cleared ?? live?.cleared ?? "uncleared",
    flag: input.flag ?? live?.flag ?? "none",
    approved: input.approved ?? live?.approved ?? true,
    location: input.location ?? live?.location,
    importNote: input.importNote ?? live?.importNote,
    importKey: input.importKey ?? live?.importKey,
    importBatchId: input.importBatchId ?? live?.importBatchId,
    scheduleId: input.scheduleId ?? live?.scheduleId,
    occurrence: input.occurrence ?? live?.occurrence,
    createdBy: live?.createdBy ?? input.createdBy,
    deleted: false,
  });

  if (oldCounterpartId && oldCounterpartId !== transfer?.txnId) {
    w.remove("transactions", oldCounterpartId);
  }
  if (transfer && splits.length === 0) {
    putCounterpart(
      w,
      transfer.txnId,
      transfer.accountId,
      txn,
      -input.amount,
      counterpartCategory,
      txn.memo,
      counterpartIncome,
    );
  }
  for (const [id] of oldSplitCounterparts) {
    if (!usedOld.has(id)) {
      w.remove("transactions", id);
    }
  }
  for (const s of splits) {
    if (s.transferAccountId && s.transferTxnId) {
      const other = account(w, s.transferAccountId);
      const cat = !own.onBudget && other.onBudget ? s.categoryId : undefined;
      putCounterpart(w, s.transferTxnId, other.id, txn, -s.amount, cat, s.memo);
    }
  }
  return txn;
}

interface CounterpartPlacement {
  accountId: AccountId;
  amount: number;
}

/**
 * A reconciled counterpart is locked like the transaction itself (SPEC §6): editing one side
 * must not move, re-amount or delete the other side's reconciled balance unless confirmed.
 */
function guardReconciledCounterpart(
  w: DocWriter,
  id: string,
  next: CounterpartPlacement | null,
  date: IsoDate,
): void {
  const c = w.get("transactions", id);
  if (!c || c.deleted || c.cleared !== "reconciled") {
    return;
  }
  if (
    !next ||
    c.accountId !== next.accountId ||
    c.amountAndSplits.amount !== next.amount ||
    c.date !== date
  ) {
    throw new ReconciledLockedError();
  }
}

function putCounterpart(
  w: DocWriter,
  id: TransactionId,
  accountId: AccountId,
  source: Transaction,
  amount: number,
  categoryId: CategoryId | undefined,
  memo: string,
  income?: Income,
): void {
  const existing = w.get("transactions", id);
  // A derived ID can name a tombstone or a counterpart in another account: start it afresh.
  const live = existing && !existing.deleted && existing.accountId === accountId ? existing : null;
  w.put("transactions", id, {
    accountId,
    date: source.date,
    amountAndSplits: { amount, splits: [] },
    transfer: { accountId: source.accountId, txnId: source.id },
    payeeId: undefined,
    categoryId,
    income: categoryId ? undefined : income,
    memo: live ? live.memo : memo,
    cleared: live?.cleared ?? "uncleared",
    flag: live?.flag ?? "none",
    approved: live?.approved ?? true,
    createdBy: live?.createdBy ?? source.createdBy,
    ...(live
      ? {}
      : {
          importKey: undefined,
          importBatchId: undefined,
          importNote: undefined,
          location: undefined,
          scheduleId: undefined,
          occurrence: undefined,
        }),
    deleted: false,
  });
}

/** A split-line counterpart only accepts edits that don't change the split. */
function saveSplitCounterpart(
  w: DocWriter,
  live: Transaction,
  input: TransactionInput,
): Transaction {
  if (
    input.amount !== live.amountAndSplits.amount ||
    input.date !== live.date ||
    input.accountId !== live.accountId ||
    (input.splits?.length ?? 0) > 0
  ) {
    throw new CommandError("This transfer is part of a split. Edit it from the split transaction.");
  }
  return w.put("transactions", live.id, {
    memo: input.memo ?? live.memo,
    cleared: input.cleared ?? live.cleared,
    flag: input.flag ?? live.flag,
    approved: input.approved ?? live.approved,
    importNote: input.importNote ?? live.importNote,
    importKey: input.importKey ?? live.importKey,
    importBatchId: input.importBatchId ?? live.importBatchId,
  });
}

/** Converts a stored transaction back into input form, for edits that change a few fields. */
export function toInput(t: Transaction): TransactionInput {
  const input: TransactionInput = {
    id: t.id,
    accountId: t.accountId,
    date: t.date,
    amount: t.amountAndSplits.amount,
    splits: t.amountAndSplits.splits.map((s) => {
      const out: SplitInput = { amount: s.amount, memo: s.memo };
      if (s.categoryId) {
        out.categoryId = s.categoryId;
      }
      if (s.income) {
        out.income = s.income;
      }
      if (s.transferAccountId) {
        out.transferAccountId = s.transferAccountId;
      }
      if (s.payeeId) {
        out.payeeId = s.payeeId;
      }
      return out;
    }),
    memo: t.memo,
    cleared: t.cleared,
    flag: t.flag,
    approved: t.approved,
  };
  const optional = {
    payeeId: t.payeeId,
    categoryId: t.categoryId,
    income: t.income,
    transferAccountId: t.transfer?.accountId,
  };
  for (const [k, v] of Object.entries(optional)) {
    if (v !== undefined) {
      (input as unknown as Record<string, unknown>)[k] = v;
    }
  }
  return input;
}

/** Applies a partial change to an existing transaction through saveTransaction. */
export function updateTransaction(
  w: DocWriter,
  id: TransactionId,
  change: Partial<Omit<TransactionInput, "id">>,
  options: SaveOptions = {},
): Transaction {
  const t = w.get("transactions", id);
  if (!t || t.deleted) {
    throw new CommandError("Transaction not found");
  }
  if (splitParentOf(w, t)) {
    return saveSplitCounterpart(w, t, { ...toInput(t), ...change, id });
  }
  return saveTransaction(w, { ...toInput(t), ...change, id }, options);
}

/** Deletes a transaction and its transfer counterparts (SPEC §10). */
export function deleteTransaction(
  w: DocWriter,
  id: TransactionId,
  options: SaveOptions = {},
): void {
  const t = w.get("transactions", id);
  if (!t || t.deleted) {
    return;
  }
  const parent = splitParentOf(w, t);
  if (!options.confirmReconciled) {
    const counterparts = parent
      ? []
      : [
          ...(t.transfer ? [t.transfer.txnId] : []),
          ...t.amountAndSplits.splits.flatMap((s) => (s.transferTxnId ? [s.transferTxnId] : [])),
        ];
    const reconciled = [t, ...counterparts.map((c) => w.get("transactions", c))].some(
      (x) => x && !x.deleted && x.cleared === "reconciled",
    );
    if (reconciled) {
      throw new ReconciledLockedError();
    }
  }
  if (parent) {
    // The split line stays, uncategorised, so the parent still adds up.
    w.put("transactions", parent.id, {
      amountAndSplits: {
        amount: parent.amountAndSplits.amount,
        splits: parent.amountAndSplits.splits.map((s) => {
          if (s.transferTxnId !== id) {
            return s;
          }
          const { transferAccountId: _a, transferTxnId: _t, categoryId: _c, ...rest } = s;
          return rest;
        }),
      },
    });
    w.remove("transactions", id);
    return;
  }
  w.remove("transactions", id);
  if (t.transfer) {
    w.remove("transactions", t.transfer.txnId);
  }
  for (const s of t.amountAndSplits.splits) {
    if (s.transferTxnId) {
      w.remove("transactions", s.transferTxnId);
    }
  }
}

export function forgetLocation(w: DocWriter, id: TransactionId): void {
  const t = w.get("transactions", id);
  if (t?.location) {
    w.put("transactions", id, { location: undefined });
  }
}

export function setImportNote(w: DocWriter, id: TransactionId, note: ImportNote | undefined): void {
  if (!w.get("transactions", id)) {
    throw new CommandError("Transaction not found");
  }
  w.put("transactions", id, { importNote: note });
}

/** Bulk field changes that don't affect transfer pairing. */
export function setTransactionFlags(
  w: DocWriter,
  ids: TransactionId[],
  change: { cleared?: Cleared; flag?: Flag; approved?: boolean },
): void {
  for (const id of ids) {
    const t = w.get("transactions", id);
    if (!t || t.deleted) {
      continue;
    }
    if (change.cleared && t.cleared === "reconciled" && change.cleared !== "reconciled") {
      // Unreconciling is explicit in the UI; bulk clear/unclear leaves reconciled rows alone.
      continue;
    }
    w.put("transactions", id, change);
  }
}

/** Sets the category on unsplit, non-transfer transactions; others are skipped. Returns the count changed. */
export function categoriseTransactions(
  w: DocWriter,
  ids: TransactionId[],
  target: { categoryId: CategoryId } | { income: Income },
): number {
  let changed = 0;
  for (const id of ids) {
    const t = w.get("transactions", id);
    if (!t || t.deleted || t.amountAndSplits.splits.length > 0 || t.transfer) {
      continue;
    }
    w.put(
      "transactions",
      id,
      "income" in target
        ? { income: target.income, categoryId: undefined }
        : { categoryId: target.categoryId, income: undefined },
    );
    changed++;
  }
  return changed;
}
